import * as React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { DashboardHeader } from '../components/DashboardHeader';
import { QuickActions } from '../components/QuickActions';
import { AvatarCard } from '../components/AvatarCard';
import { SocialMediaCard } from '../components/SocialMediaCard';
import { LeadsCard } from '../components/LeadsCard';
import { SectionHeader } from '../components/ui/SectionHeader';
import { DashboardSelection } from '../components/DashboardSelection';
import BlogContent from './Blog/BlogContent';
import { AvatarSelection } from './AvatarSelection';
import { SendWishes } from './SendWishes';
import { SeoHealthDetailedCard } from '../components/SeoHealthDetailedCard';
import { ResponsiveWebsiteCard } from '../components/ResponsiveWebsiteCard';
import SEOContent from './SEO';
import OptimizationTable from './SEO/OptimizationTable';
import { LeadsManagement } from './LeadsManagement';
import { EditWebsiteContent } from 'features/DigitalMarketing/pages/EditWebsiteContent';
import { EDIT_WEBSITE_CONTENT_SUB_ROUTES } from 'features/DigitalMarketing/routes/editWebsiteContentRoutes';
import { UPLOAD_VIDEO_SUB_ROUTES } from 'features/DigitalMarketing/routes/uploadVideoRoutes';
import { REEL_SUB_ROUTES } from 'features/DigitalMarketing/routes/reelRoutes';
import { CREATE_POST_SUB_ROUTES } from 'features/DigitalMarketing/routes/createPostRoutes';
import { useCancelRequestsOnRouteChange } from 'features/DigitalMarketing/hooks/useCancelRequestsOnRouteChange';
import { useDashboardData } from 'features/DigitalMarketing/hooks/useDashboardData';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import conversationalAiImg from 'assets/images/conversational-ai.webp';
import brandingMaterialsImg from 'assets/images/branding-materials.webp';
import { DMButton, DMDiv, DMImage, DMStyle } from '../components/Primitives';

type LegacyDashboardFields = {
  leads_overview?: {
    total_leads?: number;
    source_distribution?: Record<string, number>;
    avatar?: { avatar_thumbnail?: string | null };
  };
  avatar?: { avatar_thumbnail?: string | null };
};

const SkeletonBlock = ({ className = '' }: { className?: string }) => (
  <DMDiv className={`animate-pulse rounded-[1vh] bg-gray-200 ${className}`} />
);

const DashboardCardSkeleton = ({
  variant = 'default',
}: {
  variant?: 'default' | 'image' | 'seo' | 'avatar';
}) => (
  <DMDiv
    className={`h-full rounded-[2vh] border border-gray-200 bg-white p-[2vh] shadow-sm transition-opacity duration-300 ${
      variant === 'seo' ? 'bg-gradient-to-br from-indigo-100 to-indigo-200' : ''
    }`}
  >
    {variant === 'image' ? (
      <DMDiv className="flex h-full flex-col justify-end gap-[1.4vh]">
        <SkeletonBlock className="h-full min-h-[28vh] w-full bg-gray-200/80" />
        <SkeletonBlock className="h-[2vh] w-[55%]" />
      </DMDiv>
    ) : variant === 'seo' ? (
      <DMDiv className="flex h-full items-center justify-between gap-[2vh]">
        <SkeletonBlock className="h-[18vh] w-[18vh] rounded-full bg-white/50" />
        <DMDiv className="flex flex-1 flex-col gap-[2vh]">
          <SkeletonBlock className="h-[2vh] w-[65%] bg-white/50" />
          <SkeletonBlock className="h-[2vh] w-[80%] bg-white/50" />
          <SkeletonBlock className="h-[2vh] w-[55%] bg-white/50" />
        </DMDiv>
      </DMDiv>
    ) : variant === 'avatar' ? (
      <DMDiv className="flex h-full flex-col gap-[2vh]">
        <SkeletonBlock className="h-[2.4vh] w-[70%]" />
        <SkeletonBlock className="h-[1.8vh] w-[90%]" />
        <DMDiv className="flex flex-1 items-center gap-[2.5vh]">
          <SkeletonBlock className="h-[18vh] w-[18vh] rounded-full" />
          <DMDiv className="flex flex-1 flex-col gap-[1.4vh]">
            <SkeletonBlock className="h-[5vh] w-full" />
            <SkeletonBlock className="h-[5vh] w-full" />
          </DMDiv>
        </DMDiv>
      </DMDiv>
    ) : (
      <DMDiv className="flex h-full flex-col gap-[1.6vh]">
        <DMDiv className="flex items-start justify-between">
          <DMDiv className="flex flex-1 flex-col gap-[1vh]">
            <SkeletonBlock className="h-[2.2vh] w-[55%]" />
            <SkeletonBlock className="h-[1.7vh] w-[85%]" />
          </DMDiv>
          <SkeletonBlock className="h-[4.8vh] w-[4.8vh] rounded-full" />
        </DMDiv>
        {Array.from({ length: 6 }).map((_, index) => (
          <DMDiv
            key={index}
            className="flex items-center justify-between gap-[2vh]"
          >
            <SkeletonBlock className="h-[1.8vh] w-[38%]" />
            <SkeletonBlock className="h-[1vh] w-[42%] rounded-full" />
          </DMDiv>
        ))}
      </DMDiv>
    )}
  </DMDiv>
);

const DashboardLoadable = ({
  isLoading,
  children,
  skeleton,
}: {
  isLoading: boolean;
  children: React.ReactNode;
  skeleton: React.ReactNode;
}) => (
  <DMDiv className="relative h-full w-full">
    <DMDiv
      className={`h-full w-full transition-opacity duration-300 ${
        isLoading
          ? 'opacity-100'
          : 'pointer-events-none absolute inset-0 opacity-0'
      }`}
    >
      {skeleton}
    </DMDiv>
    <DMDiv
      className={`h-full w-full transition-opacity duration-300 ${
        isLoading
          ? 'pointer-events-none absolute inset-0 opacity-0'
          : 'opacity-100'
      }`}
    >
      {children}
    </DMDiv>
  </DMDiv>
);

const ClickableImagePreview = ({
  src,
  alt,
  onClick,
}: {
  src: string;
  alt: string;
  onClick: () => void;
}) => (
  <DMButton
    type="button"
    onClick={onClick}
    className="h-[38.3vh] w-full cursor-pointer overflow-hidden rounded-[2vh] focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2"
    style={{ clipPath: 'inset(0 round 2vh)' }}
    aria-label={`Open ${alt} full size image`}
  >
    <DMImage
      src={src}
      alt={alt}
      className="block h-full w-full rounded-[2vh] object-cover object-top"
    />
  </DMButton>
);

export function LegacyMarketingDashboard() {
  const location = useLocation();
  useCancelRequestsOnRouteChange();
  const [previewImage, setPreviewImage] = React.useState<{
    src: string;
    alt: string;
  } | null>(null);

  const displayPathname = location.pathname;
  const isSelection =
    displayPathname === '/digital-marketing' ||
    displayPathname === '/digital-marketing/';
  const isDashboard = displayPathname.includes('/digital-marketing/dashboard');
  const isSeoView = displayPathname.includes('/digital-marketing/seo');
  const isOptimizationTable = displayPathname.includes(
    '/digital-marketing/optimization-table'
  );
  const isLeadsManagement = displayPathname.includes(
    '/digital-marketing/leads'
  );
  const isAvatarSelection = displayPathname.includes(
    '/digital-marketing/avatar-selection'
  );
  const isSendWishes = displayPathname.includes(
    '/digital-marketing/send-wishes'
  );

  const headerContent = isSelection
    ? {
        title: 'Dashboard Selection',
        description:
          'Navigate between dashboards to view different data insights.',
      }
    : {
        title: 'Digital Marketing Dashboard',
        description:
          "From Reach to Revenue, One Smart Dashboard to Accelerate Your Institution's Growth.",
      };

  const { dashboardData, isLoadingDashboardData, hasFetchedDashboardData } =
    useDashboardData(isDashboard);
  const legacyDashboardData = dashboardData as
    | (typeof dashboardData & LegacyDashboardFields)
    | null;

  const showDashboardSkeleton =
    isLoadingDashboardData || !hasFetchedDashboardData;
  const seoHealthScoreKey =
    dashboardData?.seo_health?.overall_seo_percentage ?? 'empty';

  const renderMainDashboard = () => (
    <DMDiv className="flex-1 min-h-0 flex flex-col gap-[1vh] pb-[2vh]">
      {/* ── Row 1: Social Media | Leads | Conversational AI | Branding Material ── */}
      <DMDiv className="flex gap-[2vh] flex-none h-fit">
        {/* Social Media Management */}
        <DMDiv className="w-[20%] shrink-0 flex flex-col gap-[0.8vh]">
          <SectionHeader title="Social Media Management" />
          <DMDiv className="flex-1 h-full">
            <DashboardLoadable
              isLoading={showDashboardSkeleton}
              skeleton={<DashboardCardSkeleton variant="default" />}
            >
              <SocialMediaCard />
            </DashboardLoadable>
          </DMDiv>
        </DMDiv>

        {/* Leads Management */}
        <DMDiv className="w-[34%] shrink-0 flex flex-col gap-[0.8vh]">
          <SectionHeader title="Leads Management" />
          <DMDiv className="flex-1 h-full">
            <DashboardLoadable
              isLoading={showDashboardSkeleton}
              skeleton={<DashboardCardSkeleton variant="default" />}
            >
              <LeadsCard data={legacyDashboardData?.leads_overview} />
            </DashboardLoadable>
          </DMDiv>
        </DMDiv>

        {/* Conversational AI – Coming Soon */}
        {/* <div className="flex-1 flex flex-col gap-[0.8vh]"> */}
        <DMDiv className="flex-1 flex flex-col">
          {/* <ConversationalAICard /> */}
          <DashboardLoadable
            isLoading={showDashboardSkeleton}
            skeleton={<DashboardCardSkeleton variant="image" />}
          >
            <ClickableImagePreview
              src={conversationalAiImg}
              alt="Conversational AI"
              onClick={() =>
                setPreviewImage({
                  src: conversationalAiImg,
                  alt: 'Conversational AI',
                })
              }
            />
          </DashboardLoadable>
        </DMDiv>

        {/* Branding Material – Coming Soon */}
        {/* <div className="flex-1 flex flex-col gap-[0.8vh]"> */}
        <DMDiv className="flex-1 flex h-[38.3vh] flex-col overflow-hidden rounded-[2vh] border border-gray-200 bg-white shadow-sm">
          {/* <BrandingMaterialCard /> */}
          <DashboardLoadable
            isLoading={showDashboardSkeleton}
            skeleton={<DashboardCardSkeleton variant="image" />}
          >
            <ClickableImagePreview
              src={brandingMaterialsImg}
              alt="Branding Materials"
              onClick={() =>
                setPreviewImage({
                  src: brandingMaterialsImg,
                  alt: 'Branding Materials',
                })
              }
            />
          </DashboardLoadable>
        </DMDiv>
      </DMDiv>

      {/* ── Row 2: Digital Avatar | Website Overview (SEO Health & Responsive Website) ── */}
      <DMDiv className="flex-none h-fit flex gap-[2vh]">
        {/* Digital Avatar Management */}
        <DMDiv className="w-[32%] shrink-0 flex flex-col gap-[0.8vh]">
          <SectionHeader title="Digital Avatar Management" />
          <DMDiv className="flex-1 min-h-0">
            <DashboardLoadable
              isLoading={showDashboardSkeleton}
              skeleton={<DashboardCardSkeleton variant="avatar" />}
            >
              <AvatarCard
                avatarThumbnail={
                  legacyDashboardData?.leads_overview?.avatar?.avatar_thumbnail ||
                  legacyDashboardData?.avatar?.avatar_thumbnail ||
                  undefined
                }
              />
            </DashboardLoadable>
          </DMDiv>
        </DMDiv>

        {/* Website Overview Section */}
        <DMDiv className="w-[29%] flex flex-col gap-[0.8vh]">
          <SectionHeader title="WEBSITE OVERVIEW" />
          <DMDiv className="h-full">
            <DashboardLoadable
              isLoading={showDashboardSkeleton}
              skeleton={<DashboardCardSkeleton variant="seo" />}
            >
              <SeoHealthDetailedCard
                key={seoHealthScoreKey}
                data={dashboardData?.seo_health}
              />
            </DashboardLoadable>
          </DMDiv>
        </DMDiv>
        {/* Responsive Website Card */}
        <DMDiv className="flex-1 shrink-0">
          <DashboardLoadable
            isLoading={showDashboardSkeleton}
            skeleton={<DashboardCardSkeleton variant="image" />}
          >
            <ResponsiveWebsiteCard />
          </DashboardLoadable>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );

  return (
    <>
      {/* <div className="flex h-screen bg-neutral-20 font-poppins overflow-hidden"> */}
      {/* <Sidebar activeView={activeView} /> */}

      <DMDiv
        as="main"
        className={`digital-marketing-route relative flex flex-col px-[4vh] font-inter ${
          isSeoView ||
          isOptimizationTable ||
          isLeadsManagement ||
          isAvatarSelection ||
          isSendWishes
            ? 'h-full min-h-0 overflow-hidden'
            : isDashboard
            ? 'h-screen overflow-hidden'
            : 'h-full'
        }`}
      >
        {/* Persistent Dashboard View - kept in DOM to prevent iframe reload */}
        <DMDiv
          className={`${
            isDashboard
              ? 'flex-1 flex flex-col min-h-0 overflow-hidden digital-marketing-route-panel'
              : 'hidden'
          }`}
        >
          <DMDiv className="shrink-0">
            <DashboardHeader
              title={headerContent.title}
              description={headerContent.description}
            />
            <QuickActions />
          </DMDiv>
          {renderMainDashboard()}
        </DMDiv>

        <DMDiv
          key={isDashboard ? 'route-hidden' : displayPathname}
          className={`min-h-0 flex-1 ${
            isDashboard
              ? 'hidden'
              : 'flex flex-col digital-marketing-route-panel'
          }`}
        >
          <Routes>
            {/* Selection Route */}
            <Route
              index
              element={
                <>
                  <DMDiv className="shrink-0">
                    <DashboardHeader
                      title={headerContent.title}
                      description={headerContent.description}
                    />
                  </DMDiv>
                  <DMDiv className="flex-1 min-h-0 overflow-hidden flex flex-col">
                    <DashboardSelection />
                  </DMDiv>
                </>
              }
            />

            {/* Dashboard Route */}
            <Route path="dashboard" element={null} />

            {/* Blog Route */}
            <Route path="blog/*" element={<BlogContent />} />

            {/* SEO Route */}
            <Route path="seo" element={<SEOContent />} />
            <Route path="optimization-table" element={<OptimizationTable />} />
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="avatar-selection" element={<AvatarSelection />} />
            <Route
              path="avatar-selection/create"
              element={<AvatarSelection />}
            />
            <Route path="send-wishes" element={<SendWishes />} />

            {/* Website Content & Videos Routes */}
            <Route
              path="edit-website-content"
              element={<EditWebsiteContent />}
            />
            {EDIT_WEBSITE_CONTENT_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            {UPLOAD_VIDEO_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            {REEL_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            {CREATE_POST_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

            {/* Fallback */}
            <Route
              path="*"
              element={<Navigate to="/digital-marketing" replace />}
            />
          </Routes>
        </DMDiv>

        <DMStyle>
          {`
                        .digital-marketing-route-panel {
                            animation: digital-marketing-route-panel 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
                        }

                        @keyframes digital-marketing-route-panel {
                            0% { opacity: 0; transform: translateY(0.8vh); }
                            100% { opacity: 1; transform: translateY(0); }
                        }
                    `}
        </DMStyle>
        <ImagePreviewModal
          imageUrl={previewImage?.src || ''}
          alt={previewImage?.alt}
          onClose={() => setPreviewImage(null)}
          showNaturalSize
        />
      </DMDiv>
      {/* </div> */}
    </>
  );
}
